import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { AsyncIconButton, LoadingButton, LoadMoreOptions, Table, useEnduserObservations, useEnduserSession, usePageHeight, usePageWidth, value_is_loaded } from "@tellescope/react-components"
import { ProjectedObservation, VitalConfiguration } from "@tellescope/types-client"
import { DateTime } from "luxon"
import { Grid, InputAdornment, LinearProgress, TextField, TextFieldProps, Tooltip, Typography } from "@mui/material"
import { DateRange } from "@tellescope/types-models"
import DatePicker from "react-datepicker";
import CloseIcon from '@mui/icons-material/Close';
import { RefreshOutlined } from "@mui/icons-material"
import { useNavigationContext } from "../components/navigation"
import { VictoryAxis, VictoryChart, VictoryLegend, VictoryLine, VictoryScatter, VictoryTheme, VictoryTooltip, VictoryVoronoiContainer } from "victory"
import { classification_for_vital, color_for_classification, formatted_date, yyyy_mm_dd } from "@tellescope/utilities"
import { SECONDARY_HEX, VITAL_TITLE, VITAL_UNITS_INFO, WARNING_HEX } from "@tellescope/constants"
import { useOrganizationTheme } from "../definitions/contexts"

const CustomDateInput = forwardRef((props: TextFieldProps & { hideTimeDisplay?: boolean }, ref) => (
  <TextField fullWidth inputRef={ref} {...props} value={typeof props.value === 'string' && props.hideTimeDisplay ? props.value.split(',')[0] : props.value} />)
)

export interface DateInputProps {
  label?: string, 
  showTimeSelect?: boolean,
  hideTimeDisplay?: boolean,
  minDate?: Date,
  maxDate?: Date,
  required?: boolean,
}
export const DateOrEmptyStringInput = ({ 
  value, 
  showTimeSelect=true,
  onChange, 
  label,
  required,
  hideTimeDisplay,
  ...props
} : DateInputProps & { value?: Date | '', onChange: (d: Date | '') => void }) => {
  const inputRef = useRef(null);

  return (
    <div className="customDatePickerWidth">
    <DatePicker // wrap in item to prevent movement on focused
      {...props}
      showTimeSelect={showTimeSelect}
      selected={value ? new Date(value) : undefined}
      onChange={(from: Date) => onChange(from)} 
      dateFormat="Pp"
      autoComplete="off"
      timeIntervals={15} // 30 is default
      popperPlacement={'top'}
      customInput={<CustomDateInput hideTimeDisplay={hideTimeDisplay} fullWidth size="small" inputRef={inputRef} label={label} 
        InputProps={{
          endAdornment: (!value || required) ? undefined : (
            <InputAdornment position="end" sx={{ cursor: 'pointer' }} 
              onClick={e => {
                e.stopPropagation() // prevents opening of date picker
                onChange('')
              }}
            >
              <CloseIcon color="primary" />
            </InputAdornment>
          ), 
        }}
      />}
    />
  </div>
  )
}
export const DateRangeInput = ({ value, maxDateFrom, inputWidth, onChange, label, style, ...props } : DateInputProps & { 
  value?: DateRange, onChange: (d?: DateRange) => void 
  inputWidth?: React.CSSProperties['width'],
  style?: React.CSSProperties,
  maxDateFrom?: Date,
  hideTimeDisplay?: boolean,
}) => (
  <Grid container direction="column" style={style}>
    <Grid item sx={{ mb: '5px', }}>
      <Typography sx={{ fontSize: 15 }}>
        {label}
      </Typography>
    </Grid>

    <Grid container spacing={2} justifyContent="space-between">
    <Grid item sx={{ width: inputWidth || '50%' }}> 
    <div className="customDatePickerWidth" >
      <DateOrEmptyStringInput {...props} value={value?.from} 
        maxDate={maxDateFrom || props.maxDate}
        onChange={from => onChange({ ...value, from })}
        label="From"
      />
    </div>
    </Grid>

    <Grid item sx={{ width: inputWidth || '50%' }}> 
    <div className="customDatePickerWidth" >
      <DateOrEmptyStringInput {...props} value={value?.to} 
        onChange={to => onChange({ ...value, to })}
        label="To"
      />
    </div>
    </Grid>
  </Grid>
  </Grid>
)


const range_to_string = (r: { from: Date, to: Date }) => r.from.toString() + r.to.toString()

const useVitalsContext____internal = () => {
  const session = useEnduserSession()
  const [valuesForRange, setValuesForRange] = useState({} as Record<string, ProjectedObservation[] | string | undefined>) 
  const [range, setRange] = useState({
    from: DateTime.fromJSDate(new Date()).minus({ month: 3 }).toJSDate(),
    to:   DateTime.fromJSDate(new Date()).plus({ day: 1 }).toJSDate(), // add leeway as some readings may (incorrectly) be in the future, e.g. due to DST device bugs
  })
  const [observationsLoading] = useEnduserObservations({ dontFetch: true })

  const load = useCallback(async () => {
    const key = range_to_string(range)
    return session.api.enduser_observations.load({
      from: range.from,
      to: range.to,
      enduserId: session.userInfo.id,
    }) 
    .then(({ observations }) => setValuesForRange(v => ({ ...v, [key]: observations })))
    .catch((err: any) => {
      setValuesForRange(v => ({ ...v, [key]: err?.message ?? err?.toString() ?? 'Error loading vitals' }))
    })
  }, [session, range])

  const fetchRef = useRef({} as Record<string, boolean>)
  useEffect(() => {
    const key = range_to_string(range)
    if (fetchRef.current[key]) return
    fetchRef.current[key] = true

    load()
  }, [load, range])

  const vitals = useMemo(() => {
    const values = valuesForRange[range_to_string(range)]
    if (values === undefined || typeof values === 'string') return values

    // e.g. created manually but not loaded into range
    const otherObservationsInRange = (
      value_is_loaded(observationsLoading)
        ? observationsLoading.value.filter(v => (
          new Date(v.timestamp).getTime() >= range.from.getTime()
          && new Date(v.timestamp).getTime() <= range.to.getTime()
          && !values.find(_v => v.id === _v.id)
        ))
        : []
    )

    return [...values, ...otherObservationsInRange]
  }, [range, valuesForRange, observationsLoading])

  const modify = useCallback((
    isMatch: (v: ProjectedObservation) => boolean,
    update: (v: ProjectedObservation) => ProjectedObservation,
  ) => (
    setValuesForRange(vs => {
      const updated = { ...vs }

      for (const k in updated) {
        const value = updated[k]
        updated[k] = (
          Array.isArray(value)
            ? value.map(v => isMatch(v) ? update(v): v)
            : value
        );
      }

      return updated
    })
  ), [])

  return { load, range, setRange, vitals, modify, }
}
const VitalsContext = React.createContext({ } as ReturnType<typeof useVitalsContext____internal>)
export const WithVitalsContext = ({ children } : { children: React.ReactNode }) => (
  <VitalsContext.Provider value={useVitalsContext____internal()}>
    {children}
  </VitalsContext.Provider>
)
export const useVitalsContext = () => useContext(VitalsContext)

export const combine_vitals = (vitals: ProjectedObservation[], options={ combine: ['sbp', 'dbp'], leewayInMS: 5000 }) => {
  const { combine, leewayInMS } = options
  const withCombinations = [] as (ProjectedObservation & { matches: ProjectedObservation[] })[]

  const groups: ProjectedObservation[][] = []
  // ensure 'combine'd vitals are grouped first, so that showWithPlotsByUnit only needs to match to one
  for (const v of [...vitals].sort(v1 => (combine.includes(v1.measurement.unit.toLowerCase()) ? -1 : 0))) {
    // console.log('vital:', v.measurement.unit)

    const group = groups.find(g => g.find(c => 
         (new Date(c.timestamp).getTime() - leewayInMS <= new Date(v.timestamp).getTime())
      && (new Date(c.timestamp).getTime() + leewayInMS >= new Date(v.timestamp).getTime())
      && c.measurement.unit !== v.measurement.unit
      && c.enduserId === v.enduserId
      && (
        (combine.includes(v.measurement.unit.toLowerCase()) && combine.includes(c.measurement.unit.toLowerCase()))
      || (v.showWithPlotsByUnit?.includes(c.measurement.unit))
      || (c.showWithPlotsByUnit?.includes(v.measurement.unit))
      )
      && !g.find(m => m.measurement.unit === v.measurement.unit)
    ))

    // console.log('vital: group: ', group?.[0]?.measurement?.unit, group?.length)

    if (group) { group.push(v) }
    else { groups.push([v]) }
  }

  for (const group of groups) {
    // sort descending, so that SBP comes before DBP comes before BPM
    const [first, ...rest] = group.sort((m1, m2) => m2.measurement.unit.toString().localeCompare(m1.measurement.unit))
    withCombinations.push({ ...first, matches: [first, ...rest] })
  }

  return withCombinations
}

const VitalsTable = ({ vitals, loadMore, doneLoading, height, bolded, memoryId='vitals-table' } : { 
  vitals: ProjectedObservation[], 
  showEnduserColumn?: boolean
  showAcknowledgement?: boolean,
  loadMore?: (options?: LoadMoreOptions<ProjectedObservation> | undefined) => Promise<void>, 
  doneLoading?: () => boolean,
  height?: number,
  bolded?: string[],
  memoryId?: string,
}) => {
  const [selected, setSelected] = useState<string[]>([])
  const [allSelected, setAllSelected] = useState<boolean>(false)
  const pageHeight = usePageHeight() 
  const { sidebarOpen } = useNavigationContext()

  const filtered = useMemo(() => (
    // combine blood pressure readings onto same entry
    combine_vitals(vitals.filter(v => v.status !== 'entered-in-error'))
  ), [vitals])

  const configurations: VitalConfiguration[] = []

  return (
    <Table items={filtered} loadMore={loadMore} doneLoading={doneLoading} memoryId={memoryId}
      selectable selected={selected} setSelected={setSelected} allSelected={allSelected} setAllSelected={setAllSelected} allowUnselectItemsAfterSelectAll
      noPaper
      // title={"Readings"}
      sort={[{ ascending: false, field: 'Timestamp', type: 'number' }]}
      emptyText={`No vitals found`}
      pageOptions={{ paginated: false }}
      virtualization={{
        virtualize: true,
        widthOffset: sidebarOpen ? 250 : 150,
        rowHeight: 45,
        height: height || (pageHeight - 300),
        width: '100%',
      }} 
      // onExport={compact ? undefined : ({ labels, data }) => handle_basic_csv_export(labels, data)}
      fields={[
        {
          key: 'timestamp', label: "Timestamp",
          getSortValue: v => new Date(v.timestamp).getTime(),
          getExportData: v => formatted_date(new Date(v.timestamp)),
          width: 180,
          render: v => (
            <Grid container alignItems={"center"} spacing={0.5}>
              <Grid item>
              <Typography noWrap sx={{ fontSize: 13, width: 155, fontWeight: bolded?.includes(v.id) ? 'bold' : undefined }}>
                {formatted_date(new Date(v.timestamp))}
              </Typography>
              </Grid>

              {v.reviewedAt && 
                <Grid item>
                <Typography noWrap sx={{ fontSize: 10, width: 155, fontWeight: bolded?.includes(v.id) ? 'bold' : undefined }}>
                  Reviewed {formatted_date(new Date(v.reviewedAt))}
                </Typography>
                </Grid>
              }
            </Grid>
          )
        },
        {
          key: 'value', label: "Values", textAlign: 'center',
          getSortValue: v => v.measurement.value,
          getExportData: v => v.measurement.value,
          width: 190, style: { paddingRight: 10 },
          render: v => (
            <Grid container spacing={0.5}>
            {(v.matches.length > 1 ? v.matches : [v]).map(v => {
              const cell = (
                <Grid item xs={4} key={v.id}>
                <Typography noWrap sx={{ 
                  fontSize: 14, width: 50,
                  fontWeight: bolded?.includes(v.id) ? 'bold' : undefined,
                  backgroundColor: color_for_classification(classification_for_vital(v, configurations) || 'Unknown'),
                  p: 0.5, borderRadius: 1, color: 'black', textAlign: 'center',
                  textDecoration: v.status === 'entered-in-error' ? 'line-through' : undefined,
                }}>
                  {v.measurement.value}
                </Typography>
                </Grid>
              )

              if (v.invalidationReason) {
                return (
                  <Tooltip title={v.invalidationReason} key={v.id}>
                    {cell}
                  </Tooltip>
                )
              }

              return cell
            })}
            </Grid>
          )
        },
        // {
        //   key: 'classification2', label: "Classification", textAlign: 'center',
        //   filterType: 'multi',
        //   getFilterValue: v => (v.matches.length > 1 ? v.matches : [v]).map(v => classification_for_vital(v, configurations) || 'Unknown'),
        //   getExportData: v => classification_for_vital(v, configurations) || 'Unknown',
        //   getSortValue: v => Math.max(
        //     ...(
        //       v.matches.length > 1
        //         ? v.matches
        //         : [v]
        //     )
        //     .map(v => {
        //       const classification = classification_for_vital(v, configurations) || 'Unknown'

        //       return (
        //         classification === "Critical High" ? 10
        //       : classification === "Critical Low" ? 9
        //       : classification === "Very High" ? 8
        //       : classification === "Very Low" ? 7
        //       : classification === "High" ? 6
        //       : classification === "Low" ? 5
        //       : classification === "Target" ? 4
        //       : 0
        //       )
        //     })
        //   ),
        //   width: 160,
        //   render: v => {
        //     const classifications = (
        //       v.matches.length > 1
        //         ? v.matches
        //         : [v] 
        //     ).map(v => classification_for_vital(v, configurations) || 'Unknown')

        //     const classification = (
        //       classifications.find(c => c.includes("Critical"))// high or low
        //       || classifications.find(c => c.includes("Very")) // high or low
        //       || classifications.find(c => c.includes("High"))
        //       || classifications.find(c => c.includes("Low"))
        //       || classifications[0]
        //     )
            
        //     return (
        //       <Typography noWrap sx={{ 
        //         fontSize: 14, width: 110, backgroundColor: color_for_classification(classification),
        //         p: 0.5, borderRadius: 1, color: 'black', textAlign: 'center',
        //       }}>
        //         {classification}
        //       </Typography>
        //     )
        //   }
        // },
        {
          key: 'unit', label: "Units",
          getSortValue: v => v.measurement.unit,
          getFilterValue: v => v.matches.map(m => m.measurement.unit),
          getExportData: v => v.measurement.unit,
          width: 160,
          render: v => (
            <Grid container spacing={0.5}>
            {(v.matches.length > 1 ? v.matches : [v]).map(v => 
              <Grid item xs={4} key={v.id}>
              <Typography noWrap sx={{ fontSize: 12.5, fontWeight: bolded?.includes(v.id) ? 'bold' : undefined }}>
                {v.measurement.unit}
              </Typography>
              </Grid>
            )}
            </Grid>
          )
        },
      ]} 
    />
  )
}

const COLORS = [SECONDARY_HEX, "#87E081", WARNING_HEX]

export const VitalsPlot = ({ vitals } : { vitals: ProjectedObservation[] }) => {
  const { range } = useVitalsContext()
  const pageWidth = usePageWidth()
  const pageHeight = usePageHeight()
  const { sidebarOpen } = useNavigationContext()

  const width = pageWidth - (sidebarOpen ? 150 : 50 )

  const dataForUnit = useMemo(() => {
    const data = {} as Record<string, { x: number, y: number, unit: string, label?: string, fill?: string, beforeMeal?: boolean  }[]>
    for (const v of vitals) {
      const unit = (
        v.measurement.unit === 'mg/dL'
          ? `${v.measurement.unit}${v.beforeMeal === true ? ' (Before Meal)' : v.beforeMeal === false ? ' (After Meal)' : ' (Unknown Meal Status)'}`
          : v.measurement.unit
      )

      if (!data[unit]) { data[unit] = [] }
      data[unit].push({
        x: new Date(v.timestamp).getTime(),
        y: v.measurement.value,
        unit: v.measurement.unit,
        beforeMeal: v.beforeMeal,
        fill: color_for_classification(classification_for_vital(v, []), COLORS[vitals.indexOf(v)]),
        // label: `${v.measurement.value}`
      })
    }
    return data
  }, [vitals])

  if (vitals.length === 0) {
    return <Typography>No readings found for the given range</Typography>
  }
  const units = Object.keys(dataForUnit)
  return ( 
    <VictoryChart theme={VictoryTheme.material}
      height={Math.min(500, pageHeight / 2 )}
      width={width - 125}
      // domainPadding={{ x: 10 }}
      domain={{ 
        x: [range.from.getTime(), range.to.getTime()], 
        y: [0, Math.max(...vitals.map(v => v.measurement.value))],
      }}
      containerComponent={
        <VictoryVoronoiContainer
          labels={({ datum }) => `${formatted_date(new Date(datum.x))}: ${datum.y}${datum.unit}${datum.beforeMeal ? ' (Before Meal)': ""}`}
          voronoiBlacklist={units}
          labelComponent={<VictoryTooltip style={{ fontSize: 20 }} />}
        />
      }
    >
      <VictoryAxis tickFormat={dateString => yyyy_mm_dd(new Date(dateString))} 
        style={{ tickLabels: { fontSize: 20 }}}
      />
      <VictoryAxis dependentAxis 
        style={{ tickLabels: { fontSize: 20 }}} 
      />

      <VictoryLegend x={125} y={10}
        orientation="vertical"
        gutter={30}
        style={{ border: { stroke: "black" }, labels: { fontSize: 20 },  title: { fontSize: 20 } }}
        data={units.map((u, i) => ({ 
          name: VITAL_UNITS_INFO[u]?.label || u,
          symbol: { fill: COLORS[i] },
        }))}
      />

      {units.map((unit, i) => (
        <VictoryLine name={unit} key={i}
          data={dataForUnit[unit]}
          style={{
            data: { stroke: COLORS[i], strokeWidth: 4.5 },
          }}
        />
      ))}

      {units.map((unit, i) => (
        <VictoryScatter key={i}
          data={dataForUnit[unit]}
          size={7} 
          style={{
            data: { fill: ({ datum }) => datum.fill }
          }}
        />
      ))}
    </VictoryChart>
  )
}

const groupVitals = (vitals: ProjectedObservation[]) => {
  const grouped: { label: string, vitals: ProjectedObservation[] }[] = []

  for (const v of [...vitals].sort()) {
    const labels = v.showWithPlotsByUnit?.length ? [] : (
      (v.measurement.unit === 'DBP' || v.measurement.unit === 'SBP')
        ? ['Blood Pressure']
    : v.measurement.unit === 'mg/dL'
        ? ['Blood Glucose (mg/dL)']
    : v.measurement.unit === 'mmol/L'
        ? ['Blood Glucose (mmol/L)']
    : v.measurement.unit === 'LB'
        ? ['Weight (pounds)']
    : v.measurement.unit === 'BPM'
        ? ['Heart Rate (BPM)', 'Blood Pressure', /*'O2 Sat%'*/] // reverted O2 sat for now
        : [v.measurement.unit]
    )

    for (const unit of v.showWithPlotsByUnit || []) {
      labels.push(
        (unit === 'DBP' || unit === 'SBP')
          ? 'Blood Pressure'
      : unit === 'mg/dL'
          ? 'Blood Glucose (mg/dL)'
      : unit === 'mmol/L'
          ? 'Blood Glucose (mmol/L)'
      : unit === 'LB'
          ? 'Weight (pounds)'
      : unit === 'BPM'
          ? 'Heart Rate (BPM)'
          : unit
      )
    }

    for (const label of labels) {
      const existing = grouped.find(g => g.label === label)
      if (!existing) {
        grouped.push({ 
          label, 
          vitals: [
            v.beforeMeal === true
              ? { ...v, measurement: { ...v.measurement, unit: v.measurement.unit }}
          : v.beforeMeal === false
              ? { ...v, measurement: { ...v.measurement, unit: v.measurement.unit }}
              : v
          ]
        })
      } else {
        existing.vitals.push(v)
      }
    }
  }

  return (
    grouped
    .sort((g1, g2) => g1.label.localeCompare(g2.label))
    // filter out heart-rate-only plots (shown in Blood Pressure plots)
    .filter(v => v.label !== 'Heart Rate (BPM)')
  )
}

const VitalsDisplay = () => {
  const { vitals } = useVitalsContext()
  const height = usePageHeight() - 130

  const grouped = useMemo(() => (
    (vitals === undefined || typeof vitals === 'string')
      ? []
      : groupVitals(vitals)
  ), [vitals])
  
  if (vitals === undefined) {
    return <LinearProgress />
  }
  if (typeof vitals === 'string') {
    return <Typography color="error">{vitals}</Typography>
  }
  if (vitals.length === 0) {
    return <Typography color="error">To vitals found for this time range</Typography>
  }
  return (
    <Grid container direction="column" wrap="nowrap" sx={{ height, overflowY: 'auto' }} spacing={2}>
    {grouped.map(({ label, vitals }) => (
      <Grid item key={label}>
      <Grid container spacing={1}>
        <Grid item xs={12} sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 'bold' }}>
            {label}
          </Typography>
        </Grid>

        <Grid item xs={12} lg={6}>
          <VitalsTable memoryId={`labeled-vitals-${label}`} height={250} vitals={vitals} />
        </Grid>

        <Grid item xs={12} lg={6}>
          <VitalsPlot vitals={vitals} /> 
        </Grid>
      </Grid>
      </Grid>
    ))}
    </Grid>
  )
}

export const ConnectJunction = () => {
  const session = useEnduserSession()
  const { theme } = useOrganizationTheme()

  if (!theme.hasConnectedVital) { return null }
  return (
    <Grid container justifyContent="center" sx={{ mb: 1, maxWidth: 300 }}>
      <LoadingButton submitText='Connect Data Sources' submittingText="Launching..."
        onClick={() => (
          session.api.integrations.proxy_read({ integration: VITAL_TITLE, type: 'link', query: window.location.href })
          .then(({ data }) => {
            window.location.href = data.link_web_url
          })
        )} 
      />
    </Grid>
  ) 
}

const VitalsPageWithContext = () => {
  const { load, range, setRange } = useVitalsContext()

  const connectJunctionComponent = useMemo(() => <ConnectJunction />, [])

  return (
    <Grid container direction={"column"} spacing={1} sx={{ p: 1 }}>
      {connectJunctionComponent &&
        <Grid item>{connectJunctionComponent}</Grid>
      }
      <Grid item>
        <Grid container alignItems="center" wrap="nowrap" sx={{ width: 550 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 18, width: 150 }}>
            Your Data
          </Typography>

          <DateRangeInput required maxDateFrom={range.to} maxDate={new Date()} value={range} onChange={d => {
            if (!d?.from) return
            if (!d?.to) return

            setRange(d as { from: Date, to: Date })
          }} />

          <AsyncIconButton label='Refresh' Icon={RefreshOutlined} action={load} />
        </Grid>
      </Grid>
      
      <Grid item>
        <VitalsDisplay />
      </Grid>
    </Grid>
  )
}

export const VitalsPage = () => (
  <WithVitalsContext>
    <VitalsPageWithContext />
  </WithVitalsContext>
)